/**
 * Phase 7.6 — GuidanceTooltip.
 *
 * The calm "?" glyph next to section eyebrows. Click/tap opens a
 * popover that answers the five emotional questions:
 *   - What am I looking at?
 *   - Why does this matter?
 *   - What level of attention is appropriate? (rendered as a chip)
 *   - What happens next?
 *   - Am I safe to explore this?
 *
 * Renders nothing when the key is not registered — page never breaks.
 *
 * Usage:
 *   <GuidanceTooltip guidanceKey="analytics.outliers.intro" />
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle, X } from "lucide-react";
import { useGuidance } from "./GuidanceProvider";
import { OperationalChip } from "./OperationalChip";

export const GuidanceTooltip = ({
    guidanceKey,
    placement = "bottom",
    testId,
}) => {
    const { t } = useTranslation();
    const guidance = useGuidance(guidanceKey);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    // Close on outside click / ESC.
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("mousedown", onDown);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("mousedown", onDown);
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!guidance) return null;

    const dataTestId = testId || `ob-guidance-${guidanceKey.replace(/\./g, "-")}`;

    return (
        <span ref={containerRef} className="relative inline-flex items-center align-middle">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-label={t("ob.guidance.tooltip_aria")}
                data-testid={`${dataTestId}-trigger`}
                className="inline-flex items-center justify-center rounded-full w-4 h-4 ml-1.5 transition-colors hover:bg-white/5"
                style={{ color: "var(--ob-text-tertiary)" }}
            >
                <HelpCircle size={11} strokeWidth={1.75} aria-hidden="true" />
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-label={t("ob.guidance.popover_aria")}
                    data-testid={dataTestId}
                    className={`absolute z-50 w-80 sm:w-96 rounded-2xl p-5 shadow-2xl ${
                        placement === "bottom" ? "top-6" : "bottom-6"
                    } left-0 sm:left-auto sm:right-0`}
                    style={{
                        background: "rgba(20, 24, 36, 0.96)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        color: "var(--ob-text-primary)",
                    }}
                >
                    <div className="flex items-start justify-between gap-2 mb-3">
                        <OperationalChip
                            attention={guidance.attention_level}
                            testId={`${dataTestId}-attention`}
                        />
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label={t("ob.guidance.close")}
                            data-testid={`${dataTestId}-close`}
                            className="rounded-full p-1 hover:bg-white/5"
                            style={{ color: "var(--ob-text-tertiary)" }}
                        >
                            <X size={12} strokeWidth={1.5} />
                        </button>
                    </div>

                    {guidance.what && (
                        <Block
                            label={t("ob.guidance.q_what")}
                            text={guidance.what}
                            testId={`${dataTestId}-what`}
                        />
                    )}
                    {guidance.why && (
                        <Block
                            label={t("ob.guidance.q_why")}
                            text={guidance.why}
                            testId={`${dataTestId}-why`}
                        />
                    )}
                    {guidance.what_next && (
                        <Block
                            label={t("ob.guidance.q_what_next")}
                            text={guidance.what_next}
                            testId={`${dataTestId}-what-next`}
                        />
                    )}
                    {guidance.safe_to_explore && (
                        <Block
                            label={t("ob.guidance.q_safe")}
                            text={guidance.safe_to_explore}
                            testId={`${dataTestId}-safe`}
                        />
                    )}
                </div>
            )}
        </span>
    );
};

const Block = ({ label, text, testId }) => (
    <div className="mb-3 last:mb-0">
        <p
            className="text-[10px] uppercase tracking-[0.18em] mb-1"
            style={{
                color: "var(--ob-text-tertiary)",
                fontFamily: "Outfit, sans-serif",
            }}
        >
            {label}
        </p>
        <p
            data-testid={testId}
            className="text-sm leading-relaxed"
            style={{ color: "var(--ob-text-secondary)" }}
        >
            {text}
        </p>
    </div>
);

export default GuidanceTooltip;
